import { notEmpty, theme, useFallbackStorageValue, useResponsive } from "@product/scmp-sdk";
import { useIntersectionObserver, useTimeoutEffect } from "@react-hookz/web";
import { useAtomValue } from "jotai";
import qs from "qs";
import type { FunctionComponent } from "react";
import React, { useMemo, useRef, useState } from "react";
import type { PreloadedQuery } from "react-relay";
import { graphql, useFragment, usePreloadedQuery } from "react-relay";

import { accountAtom } from "shared/lib/account/atoms";

import { query } from "scmp-app/components/content/content-reaction";
import { useReaction } from "scmp-app/components/content/content-reaction/hooks";
import type { ReactionQueryStringPayload } from "scmp-app/components/content/content-reaction/types";
import { useLoginDialogStateHelper } from "scmp-app/components/login-dialog/hooks";
import { gdprAtom } from "scmp-app/components/tracking/gdpr";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { ArticleReactionClickEventParameters } from "scmp-app/components/tracking/google-analytics-4/types";
import type { contentReactionDisplayQuery$key } from "scmp-app/queries/__generated__/contentReactionDisplayQuery.graphql";
import type { contentReactionDisplayUpvote$key } from "scmp-app/queries/__generated__/contentReactionDisplayUpvote.graphql";
import type { contentReactionQuery } from "scmp-app/queries/__generated__/contentReactionQuery.graphql";

import { UpvoteTooltipLastSeenAtLocalStorageKey } from "./consts";
import {
  Container,
  Count,
  StyledButton,
  StyledIconUpvoteOff,
  StyledIconUpvoteOn,
  StyledTooltip,
} from "./styles";

export type Props = {
  className?: string;
  /**
   * We need to store data in different store, not able to use content service fragment at here
   */
  entityId: string;
  ga4CustomizedParameter: Omit<ArticleReactionClickEventParameters, "action_type">;
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  queryReference: PreloadedQuery<contentReactionQuery>;
};

export const ContentReactionDisplay: FunctionComponent<Props> = ({
  className,
  entityId,
  ga4CustomizedParameter,
  onClick,
  queryReference,
}) => {
  const queries = usePreloadedQuery<contentReactionQuery>(query, queryReference);
  const data = useFragment<contentReactionDisplayQuery$key>(
    graphql`
      fragment contentReactionDisplayQuery on Query
      @argumentDefinitions(entityId: { type: "String!" }, isLoggedIn: { type: "Boolean!" }) {
        upvoteCount(filter: { targetEntityId: $entityId }) {
          count
          updatedAt
        }
        upvote(filter: { targetEntityId: $entityId }) @include(if: $isLoggedIn) {
          ...contentReactionDisplayUpvote
        }
      }
    `,
    queries,
  );

  const upvote = useFragment<contentReactionDisplayUpvote$key>(
    graphql`
      fragment contentReactionDisplayUpvote on Upvote {
        upvoted
        updatedAt
      }
    `,
    data.upvote ?? null,
  );

  const { handleReactionAction, isLoading } = useReaction();
  const { openLoginDialog } = useLoginDialogStateHelper();
  const { isLoggedIn } = useAtomValue(accountAtom);

  const [isUpvoted, setIsUpvoted] = useState(upvote?.upvoted ?? false);
  const [upvoteCount, setUpvoteCount] = useState(() => {
    const originalCount = data.upvoteCount?.count ?? 0;
    if (!upvote) return originalCount;
    const upvoteCountUpdatedAt = data.upvoteCount?.updatedAt ?? 0;
    const upvoteUpdatedAt = upvote?.updatedAt;
    if (upvoteUpdatedAt && upvoteCountUpdatedAt >= upvoteUpdatedAt) return originalCount;
    return Math.max(isUpvoted ? originalCount + 1 : originalCount - 1, 0);
  });

  const isTabletUp = useResponsive(theme.breakpoints.up("tablet"), false);

  const tooltipPlacement = isTabletUp ? "right-start" : "bottom-end";

  const { set: setUpvoteTooltipLastSeenAt, value: upvoteTooltipLastSeenAt } =
    useFallbackStorageValue<string>(UpvoteTooltipLastSeenAtLocalStorageKey);

  const { isAccepted } = useAtomValue(gdprAtom);

  const upvoteButtonReference = useRef<HTMLButtonElement>(null);

  // Track intersection if not seen the upvote tooltip yet
  const intersectionObserverEntry = useIntersectionObserver(
    notEmpty(upvoteTooltipLastSeenAt) ? null : upvoteButtonReference,
    {
      rootMargin: "0px 0px -82px 0px",
      threshold: [1],
    },
  );

  const needToShowTooltip = useMemo(
    () =>
      isAccepted &&
      !notEmpty(upvoteTooltipLastSeenAt) &&
      !!intersectionObserverEntry?.isIntersecting,
    [intersectionObserverEntry?.isIntersecting, isAccepted, upvoteTooltipLastSeenAt],
  );

  const handleTooltipClose = () => {
    setUpvoteTooltipLastSeenAt(new Date().toISOString());
  };

  // Auto hide after serval seconds, reset when it's not
  useTimeoutEffect(
    () => {
      handleTooltipClose();
    },
    needToShowTooltip ? 10_000 : undefined,
  );

  return (
    <Container className={className} onClick={handleTooltipClose}>
      <StyledTooltip
        arrow
        open={needToShowTooltip}
        placement={tooltipPlacement}
        PopperProps={{
          disablePortal: true,
          modifiers: [
            {
              enabled: false,
              name: "flip",
            },
          ],
        }}
        title="Enjoying this article? Upvote to see more like this."
      >
        <StyledButton
          disabled={isLoading}
          onClick={event => {
            const isActionToUpvote = !isUpvoted;
            const calculatedCount = Math.max(
              isActionToUpvote ? upvoteCount + 1 : upvoteCount - 1,
              0,
            );
            const ga4CustomizedParameter_ = {
              ...ga4CustomizedParameter,
              action_type: isActionToUpvote ? "like" : "undo_like",
            } as const;

            sendGA4Tracking({
              action: "click",
              category: "page",
              customized_parameters: ga4CustomizedParameter_,
              subcategory: "reaction",
            });

            if (isLoggedIn) {
              handleReactionAction(
                {
                  action: isActionToUpvote ? "Upvote" : "Downvote",
                  entityId,
                },
                mutationResponse => {
                  setIsUpvoted(mutationResponse.upvote.upvoted);
                  setUpvoteCount(calculatedCount);
                  onClick?.(event);
                  sendGA4Tracking({
                    action: "sys",
                    category: "page",
                    customized_parameters: {
                      ...ga4CustomizedParameter_,
                      reaction_count: `${calculatedCount}`,
                    },
                    subcategory: "reaction",
                  });
                },
              );
            } else {
              const queryStringPayload: ReactionQueryStringPayload = {
                action: {
                  reaction: {
                    ...ga4CustomizedParameter,
                    count: calculatedCount,
                    entityId,
                  },
                },
              };
              const accountUrl = new URL(window.location.href);
              accountUrl.search = qs.stringify(queryStringPayload);
              openLoginDialog({
                description:
                  "Sign in or register for free to like this article and join the conversation with our commenting feature.",
                destination: accountUrl.toString(),
                title: "Like this article?",
              });
            }
          }}
          ref={upvoteButtonReference}
        >
          {isUpvoted ? <StyledIconUpvoteOn /> : <StyledIconUpvoteOff />}
        </StyledButton>
      </StyledTooltip>
      {upvoteCount > 0 && <Count>{upvoteCount}</Count>}
    </Container>
  );
};

ContentReactionDisplay.displayName = "ContentReactionDisplay";
